/**
 * The integration layer.
 *
 * Everything below this file is a subsystem that was built in isolation against
 * `INTERFACES.md`. This file is the only place they meet: it owns the `World`, the
 * `FixedLoop`, the `Renderer`, the `AudioEngine`, the `UI` and the `InputManager`,
 * registers the 14 sim systems in the order `INTERFACES.md` pins, and implements the
 * `LoopHooks` contract.
 *
 * Three rules shape it.
 *
 *  - **The step order is the contract.** `register()` is called once, in one place, in
 *    the documented order, unconditionally. Nothing here registers a system based on
 *    runtime state, and nothing re-orders them between runs — the `World` is reset in
 *    place (`world.reset(seed)`), never rebuilt, so the registration survives every run.
 *  - **The per-frame path allocates nothing.** Every callback this file hands to a
 *    subsystem is a preallocated instance field created in the constructor; the
 *    telemetry counter struct, the frame-record scratch and the debug hook object are
 *    all built once. `render()` and `step()` contain no object literal, no closure and
 *    no string concatenation.
 *  - **The sim stays deterministic.** This file reads the wall clock and `Math.random`
 *    for presentation and for seeds, never for anything the sim consumes. The seed
 *    itself arrives from the loadout screen or the URL and is a sim *input*, not a sim
 *    read. `tests/determinism.test.ts` lints `src/sim/**`; nothing here writes there.
 */

import { cfg } from './core/config';
import { computeContentHash } from './core/content';
import { FixedLoop, STEP_MS, type LoopHooks } from './core/loop';
import { perf, type DeviceTier } from './core/perf';
import { parseSeed } from './core/rng';
import {
  KEYS,
  clear as clearSave,
  loadMeta,
  read as readSave,
  saveMeta,
  write as writeSave,
  type MetaV1,
} from './core/save';
import { createCounters as createPerfCounters, telemetry, type PerfCounters } from './core/telemetry';

import { Renderer } from './render/renderer';
import { setColorblindPreset } from './render/palette';
import type { BloomMode } from './render/passes/PostStack';
import type { PerfTier as RenderTier } from './render/quality';

import { AudioEngine } from './audio/index';
import { InputManager } from './input/index';
import { applyFlat, setInputSettingsPort } from './input/bindings';


import {
  SAVE_VERSION as UI_SAVE_VERSION,
  SETTINGS_KEY,
  UI,
  buildShareString,
  checksumOf as uiChecksumOf,
} from './ui/index';
import { ACHIEVEMENTS } from './ui/achievements';
import { makeCaptain } from './captain/seed';
import { setCaptainTraits } from './captain/traits';
import type { LoadoutSelection, ResumeInfo, RunSummary, Settings, UIAction } from './ui/types';

import type { Enemy, Vec2 } from './sim/entities';
import { World } from './sim/world';
import { displayNameOf, recomputeStats } from './sim/build';
import { hitStopDilation, stepLifecycle, applyDamageToPlayer } from './sim/damage';
import { stepCollision } from './sim/collision';
import { stepStatus, applyStatus } from './sim/status';
import { stepAnomalies } from './sim/anomalies';
import { stepPlayer } from './sim/player';
import { rebuildEnemyIndex, resetTargeting, stepTargeting, acquireTarget, enemyByEntityId, DEFAULT_ACQUISITION_RANGE } from './sim/targeting';
import { resetWeapons, stepWeapons } from './sim/weapons';
import { Behaviour, resetProjectiles, resetSpawnStats, spawnProjectile, spawnStats, stepProjectiles } from './sim/projectiles';
import {
  installEnemies,
  setEnemyProjectileSpawner,
  setPlayerDamageSink as setEnemyPlayerDamageSink,
  stepEnemies,
} from './sim/enemies';
import { resetSteerDiag } from './sim/steering';
import { directorReset, setOverpopGuardSuspended, stepDirector } from './sim/director';
import { bossReset, setBossProjectileSelfStep, spawnBoss, stepBoss } from './sim/boss';
import { fastForward, resetAutopilot } from './sim/autopilot';
import {
  hazardReset,
  setPlayerDamageSink as setHazardPlayerDamageSink,
  setStatusSink,
  stepHazards,
} from './sim/hazards';
import { installPickups, resetPickups, stepPickups } from './sim/pickups';
import { installGacha, performPull, resetGacha, stepGacha } from './sim/gacha';
import { installSalvage } from './sim/salvage';
import { buySupply } from './sim/supply';
import {
  applyPick,
  currentOffers,
  DEFAULT_STARTER_WEAPON,
  grantStarterWeapon as grantStarter,
  rerollOffers,
  rerollsRemaining,
  resetProgression,
  setUnlocked,
  STARTER_POOL,
  stepProgression,
} from './sim/progression';

/** AMD-1 touch tuning defaults (§3.5 dead-zone remap applies on top). */
const TOUCH_STICK_RADIUS_PX = 44;
const TOUCH_TAP_MS = 180;
const TOUCH_TAP_SLOP_PX = 12;

/** Projectile slots the bench's synthetic filler leaves free for real weapon fire. */
const SYNTHETIC_FIRE_RESERVE = 64;

/** Build stamp carried by the share string and every telemetry envelope. */
export const CONTENT_VERSION = '0.1.0';

/** `run.v1` snapshot cadence (§4: pause/resume mid-run via localStorage). */
const SNAPSHOT_INTERVAL_MS = 30_000;

/** §13.1.10 summary shows the four biggest damage contributors. */
const SUMMARY_WEAPON_ROWS = 4;

// ─────────────────────────────────────────────────────────────── sim step 1 ──

/**
 * Step 1 — the only sim system the integrator owns.
 *
 * R9 writes `world.input` in `sampleInput`; R1 reads `player.focus_aim` and R2 reads it
 * through `weapons.ts`/`targeting.ts`. Nothing else bridges the two, so this does.
 */
function stepInput(w: World): void {
  const p = w.player;
  const inp = w.input;
  p.focus_aim.held = inp.focusHeld;
  if (inp.focusHeld) {
    p.focus_aim.dir.x = inp.focusX;
    p.focus_aim.dir.y = inp.focusY;
  }
}

// ─────────────────────────────────────────────────────────── the debug hook ──

/** One frame as `tools/bench.mjs` reads it. */
export interface BenchFrame {
  frame: number;
  tick: number;
  frame_ms: number;
  sim_ms: number;
  gpu_ms: number;
  draw_calls: number;
  enemies_alive: number;
  projectiles_alive: number;
  particles_alive: number;
  hash_candidate_checks: number;
  steps_dropped: number;
  degradation_rung: number;
  lights_active: number;
}

export interface RestartOptions {
  seed?: number;
  godMode?: boolean;
  suspendOverpopGuard?: boolean;
  syntheticLoad?: boolean;
  difficultyId?: string;
  /**
   * Pins §9's captain, which the run seed does not.
   *
   * AMD-54 gave the captain and the hull their own seeds, taken from the loadout rather than
   * from `runSeed`, so two harness runs asking for the same run seed still draw a different
   * face and a different last line. `tools/ab-matrix.mjs` measures a screen twice to establish
   * its own noise floor, and on the defeat and summary panels that floor was landing anywhere
   * between MAE 0.0004 and 0.31 depending on whether the roll happened to repeat — which
   * underestimates the floor exactly when the signal is fake. Cycle 080 could not read those
   * cells at all.
   */
  captainSeed?: number;
  /** Pins the hull for the same reason; §9's other per-run roll. */
  hullSeed?: number;
}

export interface DensityRequest {
  enemies: number;
  projectiles: number;
  particles: number;
}

/**
 * `window.__lumen` — the surface `tools/bench.mjs` and `tools/capture.mjs` were written
 * against. Every member either tool names is here; the two `HOOK` arrays in those files
 * are the test.
 */
export interface LumenHook {
  ready: boolean;
  restart(opts?: RestartOptions): void;
  setScenario(id: string, bossPhase?: number | null): void;
  setRunTime(seconds: number): void;
  setDensity(d: DensityRequest | null): void;
  setSpeed(scale: number): void;
  setPost(mode: string): void;
  setPaused(v: boolean): void;
  setAutoBalance(on: boolean): void;
  setTier(tier: string, remember?: boolean): void;
  setRung(rung: number): void;
  hudVisible(on: boolean): void;
  drainFrames(from: number): BenchFrame[];
  frameCursor(): number;
  stats(): Record<string, unknown>;
  gpu(): { renderer: string; method: string };
  memoryMb(): number;
  game: Game;
}

declare global {
  interface Window {
    __lumen?: LumenHook;
  }
}

// ────────────────────────────────────────────────────────────────── helpers ──

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** True when this document can actually get a WebGL2 context (spine §8 graceful path). */
export function webgl2Supported(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const probe = document.createElement('canvas');
    const gl = probe.getContext('webgl2');
    if (gl === null) return false;
    const lose = gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
    return true;
  } catch {
    return false;
  }
}

interface MemoryPerformance {
  memory?: { usedJSHeapSize: number };
}

/** `run.v1` payload. Not a bit-exact sim image — see `writeSnapshot` for what it is. */
interface RunSnapshot {
  save_version: number;
  content_hash: string;
  content_version: string;
  seed: number;
  /** AMD-54. Absent in a snapshot written before rerolls split; both fall back to `seed`. */
  captain_seed?: number;
  hull_seed?: number;
  tick: number;
  difficulty_id: string;
  arena_id: string;
  policy: string;
  active_id: string;
  level: number;
  xp: number;
  xp_to_next: number;
  hp: number;
  max_hp: number;
  surge_tier: number;
  surge_progress: number;
  rerolls_used: number;
  pos: { x: number; y: number };
  weapons: { def_id: string; level: number; evolved: boolean }[];
  passives: { def_id: string; rank: number }[];
  active: string;
  counters: {
    kills: number;
    elite_kills: number;
    boss_kills: number;
    damage_dealt: number;
    damage_taken: number;
    motes_collected: number;
    peak_surge: number;
  };
}

// ───────────────────────────────────────────────────────────────────── Game ──

/**
 * Appends the ids a gate opens, skipping any already banked. The lists are small (five
 * of them, a handful of ids each) and only touched at run end, so a linear scan beats
 * carrying a Set that would have to be rebuilt from the save on every boot.
 */
function pushNew(list: string[], ids: readonly string[] | undefined): void {
  if (ids === undefined) return;
  for (let i = 0; i < ids.length; i++) {
    if (!list.includes(ids[i])) list.push(ids[i]);
  }
}

export class Game implements LoopHooks {
  readonly world: World;
  readonly loop: FixedLoop;
  readonly renderer: Renderer;
  readonly audio = new AudioEngine();
  readonly ui: UI;
  readonly input: InputManager;

  readonly contentHash: string;

  private readonly canvas: HTMLCanvasElement;

  /** Preallocated per-frame state — nothing on the render path constructs. */
  private readonly counters: PerfCounters = createPerfCounters();
  private lastFrameAt = 0;
  private lastQualityGen = -1;

  /** Run bookkeeping. */
  private runActive = false;
  private loadout: LoadoutSelection = {
    arena_id: 'arena.trench',
    difficulty_id: 'diff.standard',
    policy: 'nearest',
    active_id: 'active.dash',
    seed: 1,
  };
  private meta: MetaV1;
  private poolExhausted = 0;
  private offersShown: readonly unknown[] | null = null;
  private lastSnapshotAt = 0;
  /** `game_over` is re-subscribed per run; the bus keeps handlers across `world.reset`. */
  private offGameOver: (() => void) | null = null;

  /** Bench / capture knobs. Off in a normal session. */
  private speedScale = 1;
  private syntheticLoad = false;
  private suspendOverpopGuard = false;
  private benchMode = false;
  private density: DensityRequest | null = null;

  /** Preallocated bound callbacks — created once, never per frame. */
  private readonly cameraToWorld: (sx: number, sy: number, out: Vec2) => void;
  private readonly aimAssist: (dirX: number, dirY: number, out: Vec2) => boolean;
  private readonly onUiAction: (a: UIAction) => void;
  private readonly onVisibility: () => void;
  private readonly onBlur: () => void;
  private readonly onResize: () => void;
  private readonly onBeforeUnload: () => void;
  private readonly renderOneFrame: () => void;
  private readonly onSeekProgress: (tick: number, target: number) => void;
  private readonly frameScratch: BenchFrame[] = [];

  private readonly hook: LumenHook;

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.canvas = canvas;
    this.contentHash = computeContentHash(cfg.snapshot());

    this.world = new World(1);
    this.loop = new FixedLoop(this);
    this.loop.enableTiming(true);

    this.renderer = new Renderer(canvas);

    this.ui = new UI(uiRoot);
    this.ui.setContentVersion(CONTENT_VERSION);

    // AMD-27/AMD-50 — the UI publishes where its furniture actually sits; before this the
    // renderer kept damage numbers clear of a hard-coded full-width band instead. The plate
    // keep-out already covers the corner that constant existed for, so honouring the real
    // rectangles hands ~38 px of portrait playfield back. The renderer types these
    // structurally and imports nothing from `src/ui`, so the dependency stays one-way.
    this.renderer.hudZones = this.ui.hudZones;

    // The UI's SettingsStore is the single owner of `lumenabyss.settings.v1`. R9 reads
    // and writes its slice through this port instead of touching localStorage, so the
    // two never race on the key.
    setInputSettingsPort({
      readBindings: () => this.ui.settings.input.bindings,
      writeBindings: (flat) => {
        this.ui.settings.input.bindings = flat;
        this.persistUiSettings();
      },
      readDeadzone: () => this.ui.settings.input.gamepad_deadzone,
      writeDeadzone: (dz) => {
        this.ui.settings.input.gamepad_deadzone.inner = dz.inner;
        this.ui.settings.input.gamepad_deadzone.outer = dz.outer;
        this.persistUiSettings();
      },
      readTouch: () => null,
      writeTouch: () => {},
    });

    this.input = new InputManager(canvas);

    this.meta = loadMeta();
    this.applyUnlocks();

    this.cameraToWorld = (sx, sy, out) => this.screenToWorld(sx, sy, out);
    this.aimAssist = (dx, dy, out) => this.bestTargetDir(dx, dy, out);
    this.onUiAction = (a) => this.handleAction(a);
    this.onVisibility = () => this.handleFocusLoss();
    this.onBlur = () => this.handleFocusLoss();
    this.onResize = () => this.renderer.resize(true);
    this.onBeforeUnload = () => this.writeSnapshot();
    this.renderOneFrame = () => this.renderer.render(this.world, 0);
    this.onSeekProgress = (tick, target) => {
      if (typeof console === 'undefined') return;
      console.info(
        'lumen: seek ' + (tick / 60 / 60).toFixed(0) + ':' + String(Math.floor((tick / 60) % 60)).padStart(2, '0') +
          ' / ' + (target / 3600).toFixed(1) + 'min · ' + this.world.enemies.count + ' enemies · Lv ' +
          this.world.player.level,
      );
    };

    this.ui.onAction = this.onUiAction;
    this.input.setAimAssistProvider(this.aimAssist);

    this.wireSinks();
    this.registerSystems();

    this.world.bus.on('pool_exhausted', () => {
      this.poolExhausted++;
    });

    this.hook = this.buildHook();
    this.installLifecycle();
  }

  // ── wiring ────────────────────────────────────────────────────────────────

  /**
   * Every cross-module sink the subsystems expose, in one place.
   *
   * Two of them are deliberately left at their defaults, and both would silently break
   * a subsystem if wired the obvious way:
   *
   *  - `setEnemyProjectileSpawner` — handing R3 R2's `spawnProjectile` flips R3's
   *    `ownsProjectiles` off, but `stepProjectiles` skips every shape whose faction is
   *    not `player` (projectiles.ts:1215). Enemy fire would spawn and then never move.
   *  - `setBossProjectileSelfStep(false)` — same reason, for R4's patterns.
   *
   * They stay as they are until R2 integrates enemy-faction shapes.
   */
  private wireSinks(): void {
    // R3 → R1: enemy contact and enemy fire route through damage.ts's canonical path,
    // so dash i-frames, mercy, armour and the shield pool are applied exactly once.
    setEnemyPlayerDamageSink((w, sourceId, sourceDefId, amount) => {
      applyDamageToPlayer(w, amount, sourceId, sourceDefId);
    });
    setEnemyProjectileSpawner(null);

    // R4 → R1: hazards deal player damage and apply status through the same two owners.
    setHazardPlayerDamageSink((w, amount, sourceDefId, sourceId) => {
      applyDamageToPlayer(w, amount, sourceId, sourceDefId);
    });
    setStatusSink((w, statusId, stacks, magnitude, _durationS, sourceId) => {
      applyStatus(w, w.player, statusId, stacks, sourceId, magnitude);
    });

    setBossProjectileSelfStep(true);
  }

  /** The `INTERFACES.md` step order, once, unconditionally. */
  private registerSystems(): void {
    const w = this.world;
    w.register('input', stepInput); // 1
    w.register('player', stepPlayer); // 2
    // AMD-51 — before the director, because swarm-surge must set its accrual scale
    // before the same tick's accrue() reads it.
    w.register('anomalies', stepAnomalies); // 2b
    w.register('director', stepDirector); // 3
    w.register('enemies', stepEnemies); // 4
    w.register('boss', stepBoss); // 5
    w.register('hazards', stepHazards); // 6
    w.register('targeting', stepTargeting); // 7
    w.register('weapons', stepWeapons); // 8
    w.register('projectiles', stepProjectiles); // 9
    w.register('collision', stepCollision); // 10
    w.register('status', stepStatus); // 11
    w.register('pickups', stepPickups); // 12
    w.register('progression', stepProgression); // 13
    // AMD-35 — the Graft credits Abyssal Ichor from kills resolved earlier this tick and
    // settles a pending pull. It runs after progression so a level-up's grants land first,
    // and before lifecycle so a pull taken this tick is visible before slots are released.
    w.register('gacha', stepGacha); // 13b
    w.register('lifecycle', stepLifecycle); // 14
  }

  private installLifecycle(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  // ── boot ──────────────────────────────────────────────────────────────────

  /** Applies settings, probes the device, offers the resume prompt, starts the loop. */
  async boot(): Promise<void> {
    telemetry.contentHash = this.contentHash;
    telemetry.version = CONTENT_VERSION;

    this.readUrlOverrides();
    this.applySettings(this.ui.settings);

    perf.setGpuSignature(this.gpuRenderer());
    const tier = this.ui.settings.video.perf_tier;
    if (tier === 'auto') {
      await perf.probe(this.renderOneFrame);
    } else {
      perf.setTier(tier as DeviceTier, false);
    }
    this.syncQuality(true);
    this.renderer.resize(true);

    this.audio.installGestureUnlock();

    if (this.benchMode) {
      // A harness drives its own run and must never land on the title screen. Started
      // after the probe so the governor's opening rung is the one it measured.
      this.startRun(this.loadout.seed, this.loadout.difficulty_id, this.loadout);
    } else {
      this.ui.setResumeAvailable(this.readResumeInfo());
    }

    this.loop.start();
    this.hook.ready = true;
  }

  /**
   * `?bench=1` / `?capture=1` and their knobs. The harnesses set the scene through
   * `window.__lumen` afterwards; these only decide what boot itself does.
   */
  private readUrlOverrides(): void {
    if (typeof window === 'undefined' || typeof URLSearchParams !== 'function') return;
    const q = new URLSearchParams(window.location.search);
    if (q.get('bench') === '1' || q.get('capture') === '1') {
      this.benchMode = true;
      telemetry.enabled = true;
      this.ui.setHarnessDriven(true);
    }
    const seed = q.get('seed');
    if (seed !== null) {
      const parsed = parseSeed(seed);
      if (parsed !== null) this.loadout.seed = parsed;
    }
    if (q.get('god_mode') === '1') cfg.set('bench.god_mode', true);
    if (q.get('suspend_overpop_guard') === '1') this.setOverpopGuard(true);
    if (q.get('synthetic_load') === '1') this.syntheticLoad = true;
    const auto = q.get('auto_balance');
    if (auto !== null) perf.setAutoBalance(auto === '1');
  }

  // ── LoopHooks ─────────────────────────────────────────────────────────────

  sampleInput(_tick: number): void {
    const p = this.world.player;
    this.input.setPlayerWorldPos(p.pos.x, p.pos.y);
    this.input.sample(this.world.input, this.cameraToWorld);
  }

  step(_tick: number): void {
    this.world.step(this.loop.currentTick);
    if (this.syntheticLoad) this.injectSyntheticLoad();
  }

  flush(tick: number): void {
    this.world.bus.flush(tick);
    this.pumpLevelUp();
  }

  render(tick: number, alpha: number): void {
    const t0 = nowMs();
    const frameMs = this.lastFrameAt === 0 ? STEP_MS : t0 - this.lastFrameAt;
    this.lastFrameAt = t0;

    // AMD-55 — the presentation clock. Sim time stops dead on the last tick of a run, so
    // a renderer that derives `dt` from it freezes every effect mid-frame; the death beat
    // is entirely made of what happens after that tick. The frame's own elapsed time is
    // handed over for exactly those effects, clamped so a backgrounded tab does not
    // resume by fast-forwarding the whole beat in one step.
    /*
     * A paused frame holds still.
     *
     * `alpha` is the sub-tick interpolation fraction, and the loop keeps accumulating real
     * time into it whether or not the sim is stepping. The renderer builds its clock from
     * `(world.tick + alpha) / 60`, so with the world frozen that clock still wanders across
     * a 1/60 s band every frame — enough to move a 2 Hz pulse by ~0.2 rad. Behind a pause
     * panel or a level-up modal the field quietly shimmers; cycle 026 found it the hard way,
     * when an elite's outline ring appeared in one capture of a build and vanished in the
     * next capture of the SAME build, and the difference outweighed the change under review.
     *
     * Interpolating between a state and itself has no meaning anyway: when nothing stepped,
     * there is nothing to interpolate toward. Pinning to 0 is both the still frame and the
     * honest one.
     *
     * The presentation dt goes with it, for the same reason and with the same caveat. It is
     * what advances particles and the effects that do not read the sim clock, so leaving it
     * live meant drift kept crawling behind a pause panel and the capture bed stayed noisy at
     * some ticks even after `alpha` was pinned. AMD-55's death beat is NOT affected: `step`
     * gates on `paused || outcome !== 'running'`, two separate conditions, and the beat runs
     * under the second one with `paused` still false.
     */
    // In-run canvas chrome follows the screen, not the sim: the attract field keeps running
    // behind the menus, and its radar has no meaning until there is a run to read.
    this.renderer.setRunOnScreen(this.ui.currentScreen === 'run');
    // Composed by the UI from the OS preference and the in-game toggle; the renderer's own
    // `reduced_shake` / `reduced_flashes` flags do not carry `prefers-reduced-motion`.
    this.renderer.setReducedMotion(this.ui.reducedMotion);
    this.renderer.setTitleOnScreen(this.ui.currentScreen === 'title');
    const still = this.world.paused;
    this.renderer.render(this.world, still ? 0 : alpha, still ? 0 : Math.min(frameMs, 100) / 1000);
    this.ui.update(this.world);

    const cpuMs = nowMs() - t0;
    const gpuMs = this.renderer.stats.gpuMs;
    perf.sample(gpuMs, cpuMs, gpuMs > 0, t0);
    this.syncQuality(false);

    const c = this.counters;
    const w = this.world;
    c.frame_ms = frameMs;
    c.sim_ms = this.loop.lastSimMs;
    c.gpu_ms = gpuMs;
    c.draw_calls = this.renderer.stats.drawCalls;
    c.enemies_alive = w.counters.enemies_alive;
    c.projectiles_alive = w.counters.projectiles_alive;
    c.particles_alive = this.renderer.particles.aliveCount;
    c.hash_candidate_checks = w.counters.hash_candidate_checks;
    c.steps_dropped = this.loop.stepsDropped;
    c.degradation_rung = perf.rung;
    c.lights_active = this.renderer.graph.bossLight.intensity > 0 ? 2 : 1;
    c.pool_hw_enemies = w.enemies.highWater;
    c.pool_hw_projectiles = w.projectiles.highWater;
    c.pool_hw_pickups = w.pickups.highWater;
    c.pool_hw_particles = this.renderer.particles.aliveCount;
    telemetry.record(c, tick);

    if (this.runActive && t0 - this.lastSnapshotAt >= SNAPSHOT_INTERVAL_MS) {
      this.lastSnapshotAt = t0;
      this.writeSnapshot();
    }
  }

  /**
   * Sim-time dilation. Zero freezes the clock outright, which is how a pause, a
   * level-up modal and a finished run all stop the run timer without the loop having to
   * know about any of them: `advance()` accumulates nothing, so no tick happens, but the
   * frame is still drawn.
   */
  dilation(): number {
    if (this.world.paused || this.world.outcome !== 'running' || !this.runActive) return 0;
    return hitStopDilation(this.world) * this.speedScale;
  }

  onStepsDropped(count: number): void {
    this.world.counters.steps_dropped += count;
  }

  // ── run lifecycle ─────────────────────────────────────────────────────────

  /** §13.2: Loadout ──Begin──▶ Run. The one entry point into a live run. */
  startRun(seed: number, difficultyId: string, loadout?: LoadoutSelection): void {
    if (loadout) this.loadout = loadout;
    this.loadout.seed = seed >>> 0;
    this.loadout.difficulty_id = difficultyId;

    const w = this.world;
    w.reset(seed >>> 0);
    // AMD-54 — after `reset`, which defaults both to the run seed.
    w.captainSeed = (this.loadout.captain_seed ?? seed) >>> 0;
    w.hullSeed = (this.loadout.hull_seed ?? seed) >>> 0;
    w.difficultyId = difficultyId;

    // Per-run module state. Every one of these is a documented reset hook.
    installEnemies(w);
    directorReset(w);
    bossReset(w);
    hazardReset(w);
    resetPickups(w);
    installPickups(w, true);
    resetWeapons();
    resetTargeting();
    resetProjectiles();
    resetSpawnStats();
    resetSteerDiag();
    resetProgression(w);
    resetGacha(w);
    // AFTER installPickups: a kill's drops resolve before its ichor is credited (AMD-35).
    installGacha(w);
    // AMD-53 — after the gacha so the spin's grant lands on an installed balance.
    installSalvage(w);
    resetAutopilot(w);
    this.grantStarterWeapon();
    rebuildEnemyIndex(w, true);
    this.applyPolicy(this.loadout.policy);
    // The captain's boon and flaw have to be published before the first `recomputeStats`,
    // or the run opens on a stat block that ignores them until something else forces a
    // rebuild. Drawn from `captainSubSeed(runSeed, 'traits')` — disjoint from identity and
    // face, and outside `STREAM_NAMES` — so no gameplay draw order moves.
    setCaptainTraits(makeCaptain(w.captainSeed).traits);
    recomputeStats(w);

    this.poolExhausted = 0;
    this.offersShown = null;
    this.runActive = true;
    this.lastSnapshotAt = nowMs();

    this.loop.reset();
    this.renderer.reset();
    this.renderer.install(w);
    this.audio.bindWorld(w);
    this.input.bindWorld(w);
    this.ui.bindWorld(w);

    // After the UI so an explicit report outranks the UI's own `observer.build` call.
    if (this.offGameOver !== null) this.offGameOver();
    this.offGameOver = w.bus.on('game_over', () => this.finishRun());

    telemetry.reset();
    telemetry.begin(w.runSeed, this.contentHash, CONTENT_VERSION);

    this.meta.stats.runs++;
    this.persistMeta();

    this.input.releaseAll();
    this.input.setUiMode(false);
    this.ui.setScreen('run');
    if (!this.loop.isRunning) this.loop.start();
  }

  pauseRun(): void {
    if (!this.runActive || this.world.outcome !== 'running') return;
    // A level-up draw already owns `paused`; a blur must not steal the modal.
    if (this.world.paused) return;
    this.world.paused = true;
    this.input.releaseAll();
    this.input.setUiMode(true);
    this.audio.setMuted(true);
    this.ui.presentPause(this.world);
    this.writeSnapshot();
  }

  resumeRun(): void {
    if (!this.runActive) return;
    // A level-up draw owns `paused` while it is open; the pause menu must not steal it.
    if (currentOffers(this.world) !== null) return;
    this.world.paused = false;
    this.input.setUiMode(false);
    this.audio.setMuted(false);
    this.ui.setScreen('run');
  }

  restartRun(): void {
    this.startRun(this.loadout.seed, this.loadout.difficulty_id, this.loadout);
  }

  /** §13.2: Pause ──Abandon──▶ Title. Keeps no snapshot. */
  abandonRun(): void {
    this.endRun(false);
    clearSave(KEYS.run);
    this.ui.setResumeAvailable(null);
    this.ui.setScreen('title');
  }

  private endRun(keepSnapshot: boolean): void {
    this.runActive = false;
    this.world.paused = false;
    this.audio.setMuted(false);
    this.input.releaseAll();
    this.input.setUiMode(true);
    if (!keepSnapshot) clearSave(KEYS.run);
  }

  /** `game_over` landed: victory or defeat. The UI presents; this records. */
  private finishRun(): void {
    const w = this.world;
    const durationS = w.tick / 60;
    telemetry.end(w.tick, w.outcome, durationS);

    if (w.outcome === 'victory') {
      this.meta.stats.victories++;
      if (durationS > this.meta.stats.best_duration_s) this.meta.stats.best_duration_s = durationS;
    } else if (durationS > this.meta.stats.best_duration_s) {
      this.meta.stats.best_duration_s = durationS;
    }
    this.awardAchievements();
    this.persistMeta();

    if (w.outcome === 'defeat') {
      // §13.1.9 — all four fields, assembled from the run observer's 10 s intake ring.
      this.ui.presentDefeat(w, this.ui.observer.build(w));
    }
    this.endRun(false);
    this.ui.setResumeAvailable(null);
  }

  // ── level-up ──────────────────────────────────────────────────────────────

  /**
   * R5 banks levels in step 13, opens the draw itself and sets `world.paused`. The
   * integrator's job is to notice a *new* draw and put it on screen, and to keep the
   * modal in step when several levels land in one tick.
   */
  private pumpLevelUp(): void {
    const offers = currentOffers(this.world);
    if (offers === null) {
      if (this.offersShown !== null) {
        this.offersShown = null;
        if (this.runActive && this.world.outcome === 'running') this.ui.setScreen('run');
      }
      return;
    }
    if (offers === this.offersShown) return;
    this.offersShown = offers;
    this.presentOffers(offers);
  }

  private presentOffers(offers: readonly unknown[]): void {
    this.input.setUiMode(true);
    this.ui.presentLevelUp({
      level: this.world.player.level,
      offers: offers as never,
      rerolls_left: rerollsRemaining(this.world),
    });
  }

  private pickOffer(offerId: string): void {
    applyPick(this.world, offerId);
    this.applyPolicy(this.loadout.policy);
    const next = currentOffers(this.world);
    this.offersShown = next;
    if (next === null) {
      this.input.setUiMode(false);
      this.ui.setScreen('run');
    } else {
      this.presentOffers(next);
    }
  }

  private reroll(): void {
    const next = rerollOffers(this.world);
    if (next === null) return;
    this.offersShown = next;
    this.presentOffers(next);
  }

  /**
   * §9.9.1 — opens the run with the chosen starter. The three differ in shape rather
   * than strength (all ~10.5 DPS at L1), so this is a real opening choice and not a
   * power pick. The sim owns the grant; an absent or unknown id falls back to the
   * Lance rather than opening the run unarmed.
   */
  private grantStarterWeapon(): void {
    grantStarter(this.world, this.loadout.starter_weapon ?? DEFAULT_STARTER_WEAPON);
  }

  /** §13.1.2 — the per-run policy only binds the focus slot (§5.10), but set them all. */
  private applyPolicy(policy: string): void {
    const p = this.world.player;
    for (let i = 0; i < p.weapons.length; i++) p.weapons[i].policy = policy;
  }

  // ── settings ──────────────────────────────────────────────────────────────

  /**
   * Every video / audio / access setting, pushed to whichever subsystem owns it.
   * Called on boot and on every `settings_changed`.
   */
  applySettings(s: Settings): void {
    const v = s.video;

    const bloom: BloomMode = v.bloom_intensity <= 0.01 ? 'off' : v.bloom_intensity < 0.6 ? 'reduced' : 'default';
    this.renderer.setBloomMode(bloom);
    this.renderer.setExposure(cfg.get('render.exposure'));
    this.renderer.setVhsIntensity(v.vhs_intensity);
    this.renderer.setScanlineStrength(v.scanline_intensity);
    this.renderer.setReducedShake(v.reduced_shake);
    this.renderer.setReducedFlashes(v.reduced_flashes);
    this.renderer.setTextScale(v.text_scale);
    // §13.4 — the colourblind presets restyled the DOM but never reached the WebGL world:
    // nothing in the app called `setColorblindPreset`, so the renderer's LINEAR table was
    // never rebuilt and no preset had ever applied to the arena. The accessibility
    // commitment was half-wired from the start.
    setColorblindPreset(v.colorblind_mode);
    this.renderer.graph.refreshPalette();
    this.renderer.setDamageNumbers(v.damage_numbers);

    this.audio.setVolumes(s.audio.master, s.audio.music, s.audio.sfx);

    const dz = s.input.gamepad_deadzone;
    this.input.setDeadzones(dz.inner, dz.outer);
    // AMD-1 touch tuning defaults. The UI settings tree does not expose these yet, so
    // they come from the constants the input layer itself ships.
    this.input.setTouchConfig(TOUCH_STICK_RADIUS_PX, TOUCH_TAP_MS, TOUCH_TAP_SLOP_PX, false);
    this.applyBindings(s);

    if (v.perf_tier === 'auto') {
      perf.setAutoBalance(true);
    } else {
      perf.setTier(v.perf_tier as DeviceTier, true);
    }
    this.syncQuality(true);

    this.applyWindowMode(v.window_mode);
  }

  /**
   * R8 owns the remap table; R9 owns the live `BindingTable`. `applyFlat` speaks R9's
   * flat form, which names UI confirm/back without the `ui_` prefix.
   */
  private applyBindings(s: Settings): void {
    const flat: Record<string, { primary: string; alt: string }> = {};
    for (const key of Object.keys(s.input.bindings)) {
      const slot = s.input.bindings[key];
      const name = key === 'ui_confirm' ? 'confirm' : key === 'ui_back' ? 'back' : key;
      flat[name] = { primary: slot.primary, alt: slot.alt };
    }
    applyFlat(this.input.bindings, flat);
  }

  private applyWindowMode(mode: string): void {
    if (typeof document === 'undefined') return;
    try {
      if (mode === 'fullscreen' && document.fullscreenElement === null) {
        void document.documentElement.requestFullscreen?.();
      } else if (mode !== 'fullscreen' && document.fullscreenElement !== null) {
        void document.exitFullscreen?.();
      }
    } catch {
      // A denied fullscreen request is a user-agent decision, not a run-ending one.
    }
  }

  /** Mirrors the governor's rung/tier into the render quality state when it moves. */
  private syncQuality(force: boolean): void {
    if (!force && perf.generation === this.lastQualityGen) return;
    this.lastQualityGen = perf.generation;
    let rung = perf.rung;
    // §13.1.3 "reduced particles" is a player choice, not a governor concession: it can
    // only ever pull the ladder further down, never push it back up.
    if (this.ui.settings.video.reduced_particles && rung < 2) rung = 2;
    this.renderer.setQuality(rung, perf.tier as RenderTier);
  }

  /** Rewrites the UI-owned `settings.v1` envelope (see the constructor's note). */
  private persistUiSettings(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const data = this.ui.settings;
      localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({
          save_version: UI_SAVE_VERSION,
          written_at: new Date().toISOString(),
          checksum: uiChecksumOf(data),
          data,
        }),
      );
    } catch {
      // Quota or private mode: settings stay live for this session.
    }
  }

  // ── UI actions (§13.2 flow map) ───────────────────────────────────────────

  private handleAction(a: UIAction): void {
    switch (a.type) {
      case 'start_run':
        this.startRun(a.loadout.seed, a.loadout.difficulty_id, a.loadout);
        break;
      case 'resume_run':
        this.resumeFromSnapshot();
        break;
      case 'abandon_save':
        clearSave(KEYS.run);
        this.ui.setResumeAvailable(null);
        this.ui.setScreen('title');
        break;
      case 'pick_offer':
        this.pickOffer(a.offer_id);
        break;
      case 'reroll':
        this.reroll();
        break;
      case 'pause':
        this.pauseRun();
        break;
      case 'resume':
        this.resumeRun();
        break;
      case 'restart_run':
        this.restartRun();
        break;
      case 'abandon_run':
        this.abandonRun();
        break;
      case 'retry_same_seed':
        this.startRun(a.seed, this.loadout.difficulty_id, this.loadout);
        break;
      case 'new_run':
        this.endRun(false);
        this.ui.setScreen('loadout');
        break;
      case 'goto':
        if (a.screen === 'summary') this.ui.presentSummary(this.buildSummary());
        if (a.screen === 'title') this.endRun(this.runActive);
        break;
      case 'settings_changed':
        this.applySettings(a.settings);
        break;
      case 'replay_onboarding':
        this.ui.onboarding.start();
        break;
      case 'begin_remap':
        this.input.setUiMode(true);
        break;
      case 'restore_bindings':
        this.applyBindings(this.ui.settings);
        break;
      case 'quit':
        this.endRun(false);
        break;
      case 'graft_pull':
        // AMD-35/AMD-38 — the UI may not mutate World (INTERFACES.md rule 4), so the
        // Graft button raises an action and the pull is settled here. `performPull`
        // guards itself on `canPull`, so a stale click costs nothing.
        performPull(this.world);
        break;
      case 'supply_buy':
        // AMD-52 — same contract as the Graft: the pause panel raises, the sim settles.
        // `buySupply` guards on availability and balance, so a stale tap costs nothing.
        if (buySupply(this.world, a.item_id)) this.ui.pause.refresh(this.world);
        break;
      default:
        break;
    }
  }

  // ── summary (§13.1.10) ────────────────────────────────────────────────────

  buildSummary(): RunSummary {
    const w = this.world;
    const rows: { weapon_id: string; display_name: string; damage: number }[] = [];
    const byWeapon = w.counters.damage_by_weapon;
    for (const id of Object.keys(byWeapon).sort()) {
      rows.push({ weapon_id: id, display_name: displayNameOf(id), damage: byWeapon[id] });
    }
    rows.sort((x, y) => (y.damage === x.damage ? (x.weapon_id < y.weapon_id ? -1 : 1) : y.damage - x.damage));

    const achievements: string[] = [];
    for (const id of Object.keys(this.meta.achievements).sort()) achievements.push(id);

    const summary: RunSummary = {
      result: w.outcome === 'victory' ? 'victory' : 'defeat',
      duration_s: w.tick / 60,
      difficulty_id: w.difficultyId,
      seed: w.runSeed,
      captain_seed: w.captainSeed,
      hull_seed: w.hullSeed,
      content_version: CONTENT_VERSION,
      level: w.player.level,
      kills: w.counters.kills,
      elite_kills: w.counters.elite_kills,
      boss_kills: w.counters.boss_kills,
      motes: w.counters.motes_collected,
      peak_surge: Math.max(w.counters.peak_surge, this.ui.observer.peak),
      damage_taken: w.counters.damage_taken,
      damage_by_weapon: rows.slice(0, SUMMARY_WEAPON_ROWS),
      achievements,
    };
    return summary;
  }

  /** §13.1.10 `LA1:<seed>:<difficulty-id>:<content-version>`, plus AMD-54's seed tail. */
  shareString(): string {
    const w = this.world;
    return buildShareString(w.runSeed, w.difficultyId, CONTENT_VERSION, w.captainSeed, w.hullSeed);
  }

  // ── mid-run snapshot (§4, A.6 `run.v1`) ───────────────────────────────────

  /**
   * What a resume restores.
   *
   * Not a bit-exact sim image: the pools are 2,600 entities of mutable state and A.6
   * budgets one localStorage key, so the snapshot carries the run's *identity* — seed,
   * tick, difficulty, and the build the player has assembled. A resume replays from that
   * point with the director re-seeded at the saved minute; the swarm on screen at the
   * moment of the save does not come back. `content_hash` gates it, so a snapshot from a
   * different tuning is offered as incompatible rather than loaded (§13.1.11).
   */
  writeSnapshot(): void {
    if (!this.runActive || this.world.outcome !== 'running') return;
    const w = this.world;
    const p = w.player;
    const weapons: { def_id: string; level: number; evolved: boolean }[] = [];
    for (let i = 0; i < p.weapon_count; i++) {
      weapons.push({ def_id: p.weapons[i].def_id, level: p.weapons[i].level, evolved: p.weapons[i].evolved });
    }
    const passives: { def_id: string; rank: number }[] = [];
    for (let i = 0; i < p.passive_count; i++) {
      passives.push({ def_id: p.passives[i].def_id, rank: p.passives[i].rank });
    }
    const snap: RunSnapshot = {
      save_version: 1,
      content_hash: this.contentHash,
      content_version: CONTENT_VERSION,
      seed: w.runSeed,
      captain_seed: w.captainSeed,
      hull_seed: w.hullSeed,
      tick: w.tick,
      difficulty_id: w.difficultyId,
      arena_id: this.loadout.arena_id,
      policy: this.loadout.policy,
      active_id: this.loadout.active_id,
      level: p.level,
      xp: p.xp,
      xp_to_next: p.xp_to_next,
      hp: p.hp,
      max_hp: p.max_hp,
      surge_tier: p.lumensurge_tier,
      surge_progress: p.surge_progress,
      rerolls_used: p.rerolls_used,
      pos: { x: p.pos.x, y: p.pos.y },
      weapons,
      passives,
      active: p.active === null ? '' : p.active.def_id,
      counters: {
        kills: w.counters.kills,
        elite_kills: w.counters.elite_kills,
        boss_kills: w.counters.boss_kills,
        damage_dealt: w.counters.damage_dealt,
        damage_taken: w.counters.damage_taken,
        motes_collected: w.counters.motes_collected,
        peak_surge: w.counters.peak_surge,
      },
    };
    writeSave(KEYS.run, snap, new Date().toISOString());
  }

  readResumeInfo(): ResumeInfo | null {
    const res = readSave<RunSnapshot>(KEYS.run);
    if (!res.ok) return null;
    const s = res.payload;
    if (typeof s !== 'object' || s === null || typeof s.seed !== 'number') return null;
    return {
      time_s: s.tick / 60,
      level: s.level,
      difficulty_id: s.difficulty_id,
      compatible: s.content_hash === this.contentHash,
    };
  }

  private resumeFromSnapshot(): void {
    const res = readSave<RunSnapshot>(KEYS.run);
    if (!res.ok) {
      this.ui.setResumeAvailable(null);
      this.ui.setScreen('title');
      return;
    }
    const s = res.payload;
    this.loadout.arena_id = s.arena_id;
    this.loadout.policy = s.policy;
    this.loadout.active_id = s.active_id;
    // AMD-54 — a resume brings back the captain and the hull the run was flying, not the
    // pair the run seed alone would derive.
    this.loadout.captain_seed = s.captain_seed ?? s.seed;
    this.loadout.hull_seed = s.hull_seed ?? s.seed;
    this.startRun(s.seed, s.difficulty_id, this.loadout);

    const w = this.world;
    const p = w.player;
    // The opening draw belongs to a fresh run only.
    p.xp = s.xp;
    p.xp_to_next = s.xp_to_next;
    p.level = s.level;
    p.weapon_count = 0;
    for (let i = 0; i < s.weapons.length && i < p.weapons.length; i++) {
      p.weapons[i].def_id = s.weapons[i].def_id;
      p.weapons[i].level = s.weapons[i].level;
      p.weapons[i].evolved = s.weapons[i].evolved;
      p.weapons[i].slot = i;
      p.weapon_count++;
    }
    p.passive_count = 0;
    for (let i = 0; i < s.passives.length && i < p.passives.length; i++) {
      p.passives[i].def_id = s.passives[i].def_id;
      p.passives[i].rank = s.passives[i].rank;
      p.passive_count++;
    }
    p.active = s.active === '' ? null : { def_id: s.active, cooldown_until: 0 };
    p.rerolls_used = s.rerolls_used;
    p.lumensurge_tier = s.surge_tier;
    p.surge_progress = s.surge_progress;
    p.pos.x = s.pos.x;
    p.pos.y = s.pos.y;
    w.counters.kills = s.counters.kills;
    w.counters.elite_kills = s.counters.elite_kills;
    w.counters.boss_kills = s.counters.boss_kills;
    w.counters.damage_dealt = s.counters.damage_dealt;
    w.counters.damage_taken = s.counters.damage_taken;
    w.counters.motes_collected = s.counters.motes_collected;
    w.counters.peak_surge = s.counters.peak_surge;
    this.applyPolicy(this.loadout.policy);
    recomputeStats(w);
    p.max_hp = s.max_hp;
    p.hp = s.hp;
    this.loop.setTick(s.tick);
    w.tick = s.tick;
  }

  // ── meta / unlocks ────────────────────────────────────────────────────────

  private applyUnlocks(): void {
    const u = this.meta.unlocks;
    const ids: string[] = [];
    for (let i = 0; i < STARTER_POOL.length; i++) ids.push(STARTER_POOL[i]);
    for (const list of [u.weapons, u.passives, u.actives]) {
      for (let i = 0; i < list.length; i++) ids.push(list[i]);
    }
    setUnlocked(ids);
    this.ui.loadout.setUnlocks({ arenas: u.arenas, diff: u.diff, actives: u.actives });
  }

  /**
   * AMD-49 — settles every §13.1.11 gate against the run that just ended and banks what
   * it opens.
   *
   * Nothing had ever written `meta.achievements` or appended to `meta.unlocks`, so
   * `isUnlocked` was gating against a list only `STARTER_POOL` ever filled: 8 of 12
   * weapons, 14 of 25 passives, 2 of 3 arenas and 2 of 3 difficulty tiers were
   * unreachable in every save that has ever existed. The Loadout screen has been
   * printing three of these conditions to players the whole time.
   *
   * Runs after `finishRun` has folded the run into `meta.stats`, so a lifetime counter
   * reads the total including this run. Idempotent: an achievement already banked is
   * skipped, so re-entering the screen or finishing a second run cannot double-grant.
   */
  private awardAchievements(): void {
    const w = this.world;
    for (let i = 0; i < ACHIEVEMENTS.length; i++) {
      const a = ACHIEVEMENTS[i];
      if (this.meta.achievements[a.id] !== undefined) continue;
      const c = a.condition;
      let earned = false;
      if (c.kind === 'victory') earned = w.outcome === 'victory';
      else if (c.kind === 'victory_on') earned = w.outcome === 'victory' && this.loadout.difficulty_id === c.difficulty_id;
      // `lifetime_dashes` has no counter on `MetaV1` yet, so it can never settle here.
      // The table marks it `blocked` and the Archive says so rather than showing a bar
      // that cannot move; adding `stats.dashes` is a schema change with a migration.
      if (!earned) continue;
      this.meta.achievements[a.id] = { unlocked_at: new Date().toISOString(), run_seed: w.runSeed };
      const u = this.meta.unlocks;
      const g = a.grants;
      pushNew(u.weapons, g.weapons);
      pushNew(u.passives, g.passives);
      pushNew(u.actives, g.actives);
      pushNew(u.arenas, g.arenas);
      pushNew(u.diff, g.diff);
    }
    // The run in progress keeps its own gates; the next `applyUnlocks` picks these up.
  }

  private persistMeta(): void {
    saveMeta(this.meta, new Date().toISOString());
  }

  // ── focus / camera / targeting bridges ────────────────────────────────────

  /**
   * §13.4 pause on focus loss.
   *
   * Fires from both `blur` and `visibilitychange`, and the UI raises its own `pause`
   * action on blur too, so all three paths converge here and `pauseRun` is idempotent.
   * With the setting off, a hidden tab still goes silent — a game you cannot see should
   * not be a game you can hear.
   */
  private handleFocusLoss(): void {
    const hidden = typeof document !== 'undefined' && document.hidden;
    if (!this.runActive || this.world.outcome !== 'running') return;
    // A harness owns its own clock. `tools/review.mjs` runs two builds in two iframes, and
    // at most one of them can hold focus, so the other blurred the moment the page settled
    // and paused itself — which made a side-by-side comparison a picture of the pause
    // panel. Capture and bench pages are driven, never played; nothing about them losing
    // focus means the player looked away.
    if (this.benchMode) return;
    if (!this.ui.settings.access.pause_on_focus_loss) {
      this.audio.setMuted(hidden);
      return;
    }
    this.pauseRun();
  }

  /** CSS px on the game surface → world units. Read-only over the camera. */
  private screenToWorld(sx: number, sy: number, out: Vec2): void {
    const cam = this.renderer.camera;
    const rect = this.canvas.getBoundingClientRect();
    const w = rect.width > 0 ? rect.width : 1;
    const h = rect.height > 0 ? rect.height : 1;
    out.x = cam.x + (sx / w - 0.5) * cam.viewWidth;
    out.y = cam.y - (sy / h - 0.5) * cam.viewHeight;
  }

  /** §3.8 gamepad aim assist: the nearest legal target's direction, or nothing. */
  private bestTargetDir(dirX: number, dirY: number, out: Vec2): boolean {
    const w = this.world;
    if (!this.runActive || w.enemies.count === 0) return false;
    const p = w.player;
    const id = acquireTarget(w, 'nearest', p.pos.x, p.pos.y, DEFAULT_ACQUISITION_RANGE, 0);
    if (id === 0) return false;
    const e = enemyByEntityId(w, id);
    if (e === null) return false;
    const dx = e.pos.x - p.pos.x;
    const dy = e.pos.y - p.pos.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= 1e-6) return false;
    out.x = dx / d;
    out.y = dy / d;
    void dirX;
    void dirY;
    return true;
  }

  // ── bench synthetic load (§C.1) ───────────────────────────────────────────

  /**
   * Pads the projectile pool to the §C.1 density target.
   *
   * Runs outside `world.step`, never as a registered system, so the 14-step order stays
   * exactly what `INTERFACES.md` documents. Every parameter is derived from the sim tick,
   * so the padding is reproducible and consumes no RNG stream.
   */
  private injectSyntheticLoad(): void {
    const w = this.world;
    const want = this.density === null ? 0 : this.density.projectiles;
    if (want <= 0) return;
    // Fill to just under the B3 cap, never to it. Topping up to exactly 1,500 makes the
    // filler win every slot and the player's real weapons take the `pool_exhausted`
    // denial instead — which is the very thing §14.1 B3 asserts must not happen. The
    // reserve covers one frame of peak fire from a four-weapon evolved build (§6.7).
    let deficit = want - SYNTHETIC_FIRE_RESERVE - w.projectiles.count;
    if (deficit > 32) deficit = 32;
    const p = w.player;
    for (let i = 0; i < deficit; i++) {
      resetSpawnStats();
      const a = ((w.tick * 7 + i * 13) % 360) * (Math.PI / 180);
      spawnStats.damage = 0;
      spawnStats.speed = 10;
      spawnStats.radius = 0.3;
      spawnStats.penetration = 1;
      spawnStats.duration_s = 4;
      spawnStats.behaviour = Behaviour.Linear;
      spawnStats.visual_class = 0;
      if (spawnProjectile(w, 'bench.synthetic', p.pos.x, p.pos.y, Math.cos(a), Math.sin(a), spawnStats, p.id) === null) {
        break;
      }
    }
  }

  // ── fast-forward (§C.1 / §11.13 harness seek) ─────────────────────────────

  /**
   * `window.__lumen.setRunTime` — put the run *at* that second, not the clock.
   *
   * The old hook moved `loop.tick` and `world.tick` and nothing else, which left the
   * director's bank at zero, its schedule cursor at entry zero, the player at Lv 1 with
   * the starter lance, and the arena empty. Every §11.13 capture and every §C.1 bench
   * frame described a minute-0 run under a late-game timestamp.
   *
   * This replays the run instead: `src/sim/autopilot.ts` steps every tick from here to
   * the target with a deterministic pilot at the stick, so the director actually accrues
   * and spends its §8.2 budget, the §8.7 schedule actually fires, and the §9.3 XP curve
   * actually runs. Roughly 72,000 ticks for a 20-minute seek.
   *
   * Presentation is detached across the seek. The renderer subscribes a VFX burst to
   * every `hit` and `death` on the bus, and a 17-minute run contains tens of thousands
   * of both — all of them for frames that will never be drawn. The audio graph would
   * schedule every cue in the run at one `AudioContext.currentTime`. Neither is sim
   * state, so both are rebuilt from the world once the seek lands.
   */
  private seekRunTime(seconds: number): void {
    const target = Math.max(0, Math.round(seconds * 60));
    if (!this.runActive) this.startRun(this.loadout.seed, this.loadout.difficulty_id, this.loadout);
    const w = this.world;

    if (target <= w.tick) {
      this.loop.setTick(w.tick + 1);
      return;
    }

    this.renderer.uninstall();
    // The seek blocks the main thread for seconds. Left running, the loop would come
    // back to a `now - lastTime` of that whole interval, clamp it to 250 ms, run its
    // five steps and hand §14.5's spiral guard a dozen sim ticks to drop — charging the
    // benchmark's `steps_dropped` for wall time the sim never owed.
    const wasRunning = this.loop.isRunning;
    if (wasRunning) this.loop.stop();
    this.audio.setMuted(true);

    const t0 = nowMs();
    const res = fastForward(w, target, this.onSeekProgress);
    const elapsed = nowMs() - t0;

    this.renderer.reset();
    this.renderer.install(w);
    this.ui.bindWorld(w);
    this.audio.setMuted(false);
    // Pay the first-draw cost here rather than in the loop. The seek lands a field the
    // renderer has never drawn — late-game archetypes, elite affix visuals, hazard
    // materials — and every one of those compiles and uploads on its first frame. Left
    // to the loop that is a > 83 ms frame, which is §14.5's spiral-guard threshold and
    // shows up in the benchmark as dropped sim steps the sim never actually owed.
    this.renderOneFrame();
    this.renderOneFrame();

    // The draw modal is closed by the pilot, so nothing is pending for `pumpLevelUp`.
    this.offersShown = null;
    // `start()` re-seats `lastTime` and zeroes the accumulator, so the first frame back
    // costs one step, not the whole seek.
    this.loop.setTick(w.tick + 1);
    if (wasRunning) this.loop.start();
    this.lastSnapshotAt = nowMs();
    this.applyPolicy(this.loadout.policy);
    recomputeStats(w);

    const p = w.player;
    const secs = elapsed / 1000;
    let line =
      'lumen: seek → ' + (w.tick / 60).toFixed(1) + 's in ' + secs.toFixed(1) + 's (' +
      res.ticks + ' ticks, ' + (res.ticks / (secs > 0 ? secs : 1) / 1000).toFixed(1) + 'k ticks/s)' +
      ' · Lv ' + p.level + ' · ' + p.weapon_count + 'w/' + p.passive_count + 'p' +
      ' · ' + this.evolvedCount() + ' evolved · ' + w.enemies.count + ' enemies · ' +
      w.counters.kills + ' kills · surge ×' + p.lumensurge_tier;
    if (res.outcome !== 'running') line += ' · RUN ENDED EARLY (' + res.outcome + ')';
    if (typeof console !== 'undefined') console.info(line);
  }

  /**
   * §C.1 names `bench.suspend_overpop_guard` as a bench flag, but until now the value
   * only ever reached the artifact manifest — `stats()` reported it and the director
   * never heard about it, so §8.10's guard stayed armed through every benchmark.
   */
  private setOverpopGuard(v: boolean): void {
    this.suspendOverpopGuard = v;
    setOverpopGuardSuspended(v);
  }

  private evolvedCount(): number {
    const p = this.world.player;
    let n = 0;
    for (let i = 0; i < p.weapon_count; i++) if (p.weapons[i].evolved) n++;
    return n;
  }

  /** The live boss carrying `defId`, or null. Used to keep `setScenario` idempotent. */
  private liveBoss(defId: string): Enemy | null {
    const w = this.world;
    const list = w.enemies.liveIndices();
    for (let i = 0; i < list.length; i++) {
      const e = w.enemies.slots[list[i]];
      if (e.is_boss && e.def_id === defId) return e;
    }
    return null;
  }

  // ── window.__lumen ────────────────────────────────────────────────────────

  private buildHook(): LumenHook {
    const hook: LumenHook = {
      ready: false,
      game: this,
      restart: (opts) => {
        const o = opts ?? {};
        if (o.godMode !== undefined) cfg.set('bench.god_mode', o.godMode);
        if (o.suspendOverpopGuard !== undefined) this.setOverpopGuard(o.suspendOverpopGuard);
        if (o.syntheticLoad !== undefined) this.syntheticLoad = o.syntheticLoad;
        const seed = o.seed === undefined ? this.loadout.seed : o.seed >>> 0;
        // The loadout owns both rolls (AMD-54), so pinning them means writing them there
        // before the run reads it. Absent options leave whatever the loadout already had.
        if (o.captainSeed !== undefined) this.loadout.captain_seed = o.captainSeed >>> 0;
        if (o.hullSeed !== undefined) this.loadout.hull_seed = o.hullSeed >>> 0;
        this.startRun(seed, o.difficultyId ?? this.loadout.difficulty_id, this.loadout);
      },
      setScenario: (id, bossPhase) => this.setScenario(id, bossPhase ?? null),
      setRunTime: (seconds) => this.seekRunTime(seconds),
      setDensity: (d) => {
        this.density = d;
        if (d === null) return;
        cfg.set('budget.max_enemies', d.enemies);
        cfg.set('budget.max_projectiles', d.projectiles);
        cfg.set('budget.max_particles', d.particles);
      },
      setSpeed: (scale) => {
        this.speedScale = scale > 0 ? scale : 1;
      },
      setPost: (mode) => this.setPost(mode),
      setPaused: (v) => {
        this.world.paused = v;
        if (!v) this.ui.setScreen('run');
      },
      setAutoBalance: (on) => perf.setAutoBalance(on),
      setTier: (tier, remember) => {
        perf.setTier(tier as DeviceTier, remember === true);
        this.syncQuality(true);
      },
      setRung: (rung) => {
        perf.setRung(rung);
        this.syncQuality(true);
      },
      hudVisible: (on) => {
        this.ui.hud.root.hidden = !on;
        this.ui.onboarding.root.hidden = !on;
      },
      drainFrames: (from) => {
        const src = telemetry.drainFrames(from);
        this.frameScratch.length = 0;
        for (let i = 0; i < src.length; i++) this.frameScratch.push(src[i]);
        return this.frameScratch;
      },
      frameCursor: () => telemetry.frameCursor,
      stats: () => {
        const snap = telemetry.snapshot();
        snap.pool_exhausted = this.poolExhausted;
        snap.content_hash = this.contentHash;
        snap.suspend_overpop_guard = this.suspendOverpopGuard;
        snap.synthetic_load = this.syntheticLoad;
        return snap;
      },
      gpu: () => ({ renderer: this.gpuRenderer(), method: this.renderer.stats.gpuMs > 0 ? 'timer' : 'estimate' }),
      memoryMb: () => {
        const m = (typeof performance === 'undefined' ? undefined : (performance as unknown as MemoryPerformance).memory);
        return m === undefined ? 0 : m.usedJSHeapSize / (1024 * 1024);
      },
    };
    if (typeof window !== 'undefined') window.__lumen = hook;
    return hook;
  }

  /** `bench.stress` and the eight §11.13 encounters, addressed by id. */
  private setScenario(id: string, bossPhase: number | null): void {
    if (id.startsWith('boss.')) {
      // A seek to the encounter's scheduled minute (§8.7) has already run the
      // director's own `spawnBoss` for that tick, so asking for the same boss again
      // would put two of them in the arena. Adopt the live one instead.
      const live = this.liveBoss(id);
      if (live !== null) {
        if (bossPhase !== null) live.boss_phase_index = bossPhase;
        // Re-announce it. The director emitted `boss_spawn` mid-seek, while the HUD's
        // boss bar (§13.1.4) and the audio cues were still detached, so adopting the
        // encounter silently would photograph a boss fight with no boss bar. Every
        // consumer of this event re-attaches to the same entity idempotently.
        this.world.bus.emit('boss_spawn', { boss_id: id, entity_id: live.id });
        this.world.bus.flush(this.world.tick);
        return;
      }
      const e = spawnBoss(this.world, id);
      if (e !== null && bossPhase !== null) e.boss_phase_index = bossPhase;
      return;
    }
    if (id === 'bench.stress') {
      this.syntheticLoad = true;
      cfg.set('bench.god_mode', true);
    }
  }

  /** AMD-3 post axis: `full` is the tuned look, `off` is the raw internal buffer. */
  private setPost(mode: string): void {
    if (mode === 'off') {
      this.renderer.setBloomMode('off');
      this.renderer.setScanlineStrength(0);
      this.renderer.setVhsIntensity(0);
      return;
    }
    if (mode === 'reduced') {
      this.renderer.setBloomMode('reduced');
      this.renderer.setScanlineStrength(cfg.get('render.scanline_intensity') * 0.5);
      this.renderer.setVhsIntensity(cfg.get('render.vhs_intensity') * 0.5);
      return;
    }
    this.renderer.setBloomMode('default');
    this.renderer.setScanlineStrength(cfg.get('render.scanline_intensity'));
    this.renderer.setVhsIntensity(cfg.get('render.vhs_intensity'));
  }

  private gpuRenderer(): string {
    try {
      const gl = this.renderer.gl.getContext() as WebGL2RenderingContext;
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      if (ext === null) return String(gl.getParameter(gl.RENDERER) ?? '');
      return String(gl.getParameter((ext as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL) ?? '');
    } catch {
      return '';
    }
  }

  // ── teardown ──────────────────────────────────────────────────────────────

  dispose(): void {
    this.loop.stop();
    if (this.offGameOver !== null) {
      this.offGameOver();
      this.offGameOver = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      window.removeEventListener('blur', this.onBlur);
      window.removeEventListener('beforeunload', this.onBeforeUnload);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
      if (window.__lumen === this.hook) delete window.__lumen;
    }
    this.renderer.uninstall();
    this.ui.destroy();
    this.input.dispose();
    this.audio.dispose();
  }
}
